import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Send, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`, {}, { withCredentials: true });
      loadMessages();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-8">
          Messages
        </h1>

        {messages.length === 0 ? (
          <div className="text-center py-20 rounded-3xl bg-white border border-stone-100">
            <MessageCircle className="h-24 w-24 text-muted-foreground/30 mx-auto mb-6" />
            <h3 className="text-2xl font-heading font-semibold mb-2">No messages yet</h3>
            <p className="text-muted-foreground">Messages about marketplace listings will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.message_id}
                onClick={() => handleMarkRead(message.message_id)}
                className={`rounded-3xl bg-white border p-6 cursor-pointer transition-all hover:shadow-lg ${
                  message.read ? 'border-stone-100' : 'border-primary/30 bg-rose-50/30'
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-heading font-semibold text-lg">{message.subject}</p>
                    <p className="text-sm text-muted-foreground">
                      From: {message.from_user_name}
                    </p>
                  </div>
                  {!message.read && (
                    <span className="px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                      New
                    </span>
                  )}
                </div>
                <p className="text-base mb-3">{message.content}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(message.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
